import { isChinese } from './text-utils';

export type AbstractLocale = 'zh' | 'en';

type AbstractPreviewDoc = {
  pub_language?: string | null;
  title?: string | null;
  abstract?: string | null;
  translation?: {
    [key: string]: {
      title?: string | null;
      abstract?: string | null;
    };
  } | null;
};

export function isChinesePublicationLanguage(language?: string | null) {
  const normalized = language?.trim().toLowerCase().replaceAll('_', '-');
  return normalized === 'zh' || normalized?.startsWith('zh-') === true;
}

function normalizeComparableText(value?: string | null) {
  return (value ?? '')
    .toLowerCase()
    .replace(/[\s\u3000,，.。:：;；"'“”‘’《》<>()（）[\]【】\-—_]/g, '')
    .trim();
}

function getZhTranslation(doc: AbstractPreviewDoc) {
  return doc.translation?.['zh-cn'] ?? doc.translation?.zh ?? null;
}

function hasDuplicateChineseTranslation(doc: AbstractPreviewDoc) {
  const zhTranslation = getZhTranslation(doc);
  if (!zhTranslation) return false;

  const originalTitle = normalizeComparableText(doc.title);
  const originalAbstract = normalizeComparableText(doc.abstract);
  const zhTitle = normalizeComparableText(zhTranslation.title);
  const zhAbstract = normalizeComparableText(zhTranslation.abstract);

  return (
    Boolean(originalTitle || originalAbstract) &&
    (Boolean(originalTitle && originalTitle === zhTitle) ||
      Boolean(originalAbstract && originalAbstract === zhAbstract))
  );
}

function hasMostlyChineseOriginal(doc: AbstractPreviewDoc) {
  return isChinese(`${doc.title ?? ''}\n${doc.abstract ?? ''}`);
}

export function isEffectivelyChineseOriginal(doc: AbstractPreviewDoc) {
  if (isChinesePublicationLanguage(doc.pub_language)) {
    return true;
  }
  if (doc.pub_language?.trim()) {
    return false;
  }
  return hasDuplicateChineseTranslation(doc) || hasMostlyChineseOriginal(doc);
}

export function getAbstractPreviewLocales(
  doc: AbstractPreviewDoc,
): AbstractLocale[] {
  if (isEffectivelyChineseOriginal(doc)) {
    return ['zh'];
  }

  return ['zh', 'en'];
}

const ORIGINAL_LANGUAGE_LABELS: Record<string, string> = {
  en: '英文',
  ja: '日文',
  jp: '日文',
  de: '德文',
  fr: '法文',
  es: '西班牙文',
  it: '意大利文',
  ko: '韩文',
  ru: '俄文',
  pt: '葡萄牙文',
};

export function getOriginalLanguageLabel(language?: string | null) {
  const normalized = language?.trim().toLowerCase().replaceAll('_', '-');
  if (!normalized || isChinesePublicationLanguage(normalized)) {
    return '原文';
  }

  const primaryLanguage = normalized.split('-')[0];
  return ORIGINAL_LANGUAGE_LABELS[primaryLanguage] ?? '原文';
}

export function shouldAutoTranslateAbstract({
  locale,
  hasInitialZh,
  docId,
  deferAutoTranslate,
  pubLanguage,
  isChineseOriginal,
}: {
  locale: AbstractLocale;
  hasInitialZh: boolean;
  docId?: string | null;
  deferAutoTranslate: boolean;
  pubLanguage?: string | null;
  isChineseOriginal?: boolean;
}) {
  return (
    locale === 'zh' &&
    !hasInitialZh &&
    Boolean(docId) &&
    !deferAutoTranslate &&
    !isChineseOriginal &&
    !isChinesePublicationLanguage(pubLanguage)
  );
}
